import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  ExperienceTemplateStatus,
  IExperienceTemplate
} from '@dadohr/shared/domainTypes/experience';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import { Button } from '@material-ui/core';
import React, { useMemo, useState } from 'react';
import {
  AdditionActionsMenuOption,
  AdditionalActionsMenu
} from '../../../../../../../components/AdditionalActionsMenu';
import {
  ID_TO_ROW_KEY,
  ItemSorters,
  RowsRenderer,
  RowsRendererScrollContainer,
  ROW_HEIGHTS,
  useSortQueryParam
} from '../../../../../../../components/GroupableList';
import { PaperLoader } from '../../../../../../../components/Loader';
import {
  MultiSelector,
  MultiSelectorChip
} from '../../../../../../../components/MultiSelector';
import { IColumn } from '../../../../../../../components/Table/VirtualizedSortable';
import { TemplateStatusChip } from '../../../../../../../features/Experiences/components/TemplateStatusChip';
import { useDialogState } from '../../../../../../../hooks/useDialogState';
import { CanvasBar } from '../../../../../../../layout/Canvas';
import { FlexContainer } from '../../../../../../../layout/Flex';
import { Section } from '../../../../../../../layout/Section';
import { useExperienceTemplatesByOrgId } from '../../../../../../../services/experiences/template';
import {
  formatDatePrecise,
  toMoment
} from '../../../../../../../services/time';
import { JsonDialogInner } from '../../../../../../components/Json';
import { toFirestoreConsoleFromDoc } from '../../../../../../services/firebase';

const HEIGHT = 600;

type Props = {
  org: Doc<IOrganization>;
};

type ColumnName = 'title' | 'status' | 'tasks' | 'createdAt' | 'actions';

type D = Doc<IExperienceTemplate>;
type O = { org: Doc<IOrganization> };
type Column = IColumn<D, ColumnName, O>;

// Status filter groups. Several enum values collapse into "Draft", and — per
// requirements — a template with no (or unrecognised) status is also a Draft.
type StatusGroup = 'active' | 'archived' | 'stopped' | 'draft';

const STATUS_OPTIONS: { value: StatusGroup; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
  { value: 'stopped', label: 'Stopped' },
  { value: 'draft', label: 'Draft' }
];

const ALL_STATUS_GROUPS = STATUS_OPTIONS.map((o) => o.value);

// Default view hides Archived and Draft — most of the time only live templates
// (Active) and paused ones (Stopped/inactive) are of interest.
const DEFAULT_STATUS_GROUPS: StatusGroup[] = ['active', 'stopped'];

const toStatusGroup = (
  status: ExperienceTemplateStatus | undefined
): StatusGroup => {
  switch (status) {
    case ExperienceTemplateStatus.ACTIVE:
      return 'active';
    case ExperienceTemplateStatus.ARCHIVED:
      return 'archived';
    case ExperienceTemplateStatus.INACTIVE:
      return 'stopped';
    case ExperienceTemplateStatus.DRAFT:
    case ExperienceTemplateStatus.ROUGH_DRAFT:
      return 'draft';
    default:
      return 'draft';
  }
};

const Actions = ({ d, org }: { d: D; org: IOrganization }) => {
  const previewDialog = useDialogState(false);
  const options: AdditionActionsMenuOption[] = useMemo(() => {
    return [
      {
        key: 'openInFirestore',
        label: 'Open in Firestore console',
        externalHref: toFirestoreConsoleFromDoc(d)
      },
      {
        key: 'inspect',
        label: 'Inspect...',
        dialog: ({ onClose }) => <JsonDialogInner onClose={onClose} data={d} />
      }
    ];
  }, [d]);
  return (
    <FlexContainer spacing={0.5} justifyContent="flex-end">
      <Button
        size="small"
        variant="outlined"
        color="primary"
        onClick={(ev) => {
          ev.stopPropagation();
          ev.preventDefault();
          previewDialog.openDialog();
        }}
        disabled={true}
      >
        {'Preview (not implemented atm)'}
      </Button>
      <AdditionalActionsMenu options={options} />
    </FlexContainer>
  );
};

const COLUMNS: Column[] = [
  {
    key: 'title',
    head: () => 'Title',
    cell: (d) => d.data.adminTitle || d.data.title,
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 2
  },
  {
    key: 'status',
    head: () => 'Status',
    cell: (d) => <TemplateStatusChip status={d.data.status} />,
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 1
  },
  {
    key: 'tasks',
    head: () => '# of tasks',
    cell: (d) => d.data.tasks.length,
    align: 'left',
    sortable: true,
    defaultDirection: 'desc',
    width: 100
  },
  {
    key: 'createdAt',
    head: () => 'Created at',
    cell: (d) => formatDatePrecise(toMoment(d.data.createdAt)),
    align: 'right',
    sortable: true,
    defaultDirection: 'desc',
    width: 140,
    flexGrow: 0
  },
  {
    key: 'actions',
    head: () => '',
    alternateHead: () => 'Actions',
    cell: (d, o) => <Actions d={d} org={o.org.data} />,
    sortable: false,
    align: 'right',
    width: 150,
    flexGrow: 0
  }
];

const SORTERS: ItemSorters<D, O> = {
  name: {
    key: 'title',
    items: { sort: (d) => d.data.title, dir: 'asc' }
  },
  tasks: {
    key: 'tasks',
    items: { sort: (d) => d.data.tasks.length, dir: 'asc' }
  },
  createdAt: {
    key: 'createdAt',
    items: { sort: (d) => d.data.createdAt.toMillis(), dir: 'desc' }
  }
};

const DEFAULT_SORTER = SORTERS.createdAt;

export const Templates: React.FC<Props> = ({ org }) => {
  const [ds, loading] = useExperienceTemplatesByOrgId(org.id);
  const [[sorter, dir], setSort] = useSortQueryParam('sort', SORTERS);
  const otherProps = useMemo(() => ({ org }), [org]);
  const [statusFilter, setStatusFilter] = useState<Set<StatusGroup>>(
    () => new Set(DEFAULT_STATUS_GROUPS)
  );
  const filtered = useMemo(
    () => (ds ?? []).filter((d) => statusFilter.has(toStatusGroup(d.data.status))),
    [ds, statusFilter]
  );
  const appliedStatusLabel = statusFilter.size
    ? `Showing ${STATUS_OPTIONS.filter((o) => statusFilter.has(o.value))
        .map((o) => o.label)
        .join(', ')}`
    : 'No statuses shown';
  return (
    <Section>
      <CanvasBar>
        <div>Templates</div>
        <MultiSelector
          value={statusFilter}
          onChange={setStatusFilter}
          options={STATUS_OPTIONS}
          legend="Status"
          allOption="All statuses"
        >
          <MultiSelectorChip
            isApplied={statusFilter.size !== STATUS_OPTIONS.length}
            label="Status"
            appliedLabel={appliedStatusLabel}
            onDelete={() => setStatusFilter(new Set(ALL_STATUS_GROUPS))}
          />
        </MultiSelector>
      </CanvasBar>
      {(loading || !ds) && <PaperLoader height={HEIGHT} />}
      {!!ds && (
        <RowsRendererScrollContainer height={HEIGHT}>
          <RowsRenderer
            variant="contained"
            columns={COLUMNS}
            rows={filtered}
            rowToKey={ID_TO_ROW_KEY}
            rowToHref={(d) => `/orgs/${org.id}/experience-templates/${d.id}`}
            sorter={sorter || DEFAULT_SORTER}
            sortDirection={dir}
            renderHead={true}
            headProps={{ sticky: true }}
            onHeadClick={(c, d) =>
              setSort([SORTERS[c.key] || DEFAULT_SORTER, d])
            }
            chunkSize={30}
            rootMargin="300px"
            rowHeight={ROW_HEIGHTS.dense}
            otherProps={otherProps}
          />
        </RowsRendererScrollContainer>
      )}
    </Section>
  );
};
